import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import useLeadSubmit, { Honeypot, LeadError } from '../useLeadSubmit';
import { sectionStyles, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function WaitlistSection({ block = {}, brand, primaryColor, secondaryColor, enableAnimations, currentPage }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'centered';

    const [email, setEmail] = useState('');
    const leadSubmit = useLeadSubmit({
        brandId: brand?.id,
        blockType: 'waitlist',
        blockId: block.id,
        pageId: currentPage?.id || null,
        source: 'Lista de espera',
    });
    const submitted = leadSubmit.submitted;
    const [queuePos] = useState(() => Math.floor(Math.random() * 900) + 100);

    const title = content.title || 'Proximamente';
    const subtitle = content.subtitle || 'Se el primero en enterarte';
    const description = content.description || '';
    const placeholder = content.placeholder || 'Tu correo electronico';
    const submitText = content.submit_text || 'Unirme a la lista';
    const successMessage = content.success_message || 'Estas en la lista. Tu posicion es';
    const goal = parseInt(content.goal) || 1000;
    const currentCount = parseInt(content.current_count) || 0;
    const benefits = content.benefits || [];
    const pct = Math.min(Math.round((currentCount / goal) * 100), 100);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!email.includes('@')) return;
        if (content.cta_link && content.cta_link.startsWith('mailto:')) {
            window.location.href = `${content.cta_link}?subject=Waitlist&body=Email: ${encodeURIComponent(email)}`;
            return;
        }
        leadSubmit.submit({ email });
    };

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const ProgressBar = () => (
        <div className="mt-6">
            <div className="flex justify-between text-xs mb-1.5">
                <span style={{ color: s.textSub }}>{currentCount.toLocaleString()} inscritos</span>
                <span style={{ color: s.textSub }}>Meta: {goal.toLocaleString()}</span>
            </div>
            <div className="h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : hexToRgba(s.accent, 0.1) }}>
                <div className="h-full rounded-full transition-all duration-1000 ease-out" style={{ width: visible ? `${pct}%` : '0%', backgroundColor: s.accent }} />
            </div>
            <p className="text-xs mt-1.5 text-center" style={{ color: s.textSub }}>{pct}% completado</p>
        </div>
    );

    const BenefitsList = () => benefits.length > 0 ? (
        <ul className="space-y-2.5 mt-6">
            {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm" style={{ color: s.textSub }}>
                    <svg className="w-4.5 h-4.5 shrink-0 mt-0.5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    {b.text || b}
                </li>
            ))}
        </ul>
    ) : null;

    const SuccessView = () => (
        <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: s.accent }}>
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <p className="text-lg font-semibold mb-1" style={{ color: s.text }}>{successMessage}</p>
            <p className="text-3xl font-extrabold" style={{ color: s.accent }}>#{queuePos}</p>
        </div>
    );

    const EmailForm = ({ className = '' }) => (
        <>
            <form onSubmit={handleSubmit} className={className} style={{position:'relative'}}>
                <Honeypot onFill={leadSubmit.setHoneypot} />
                <div className="flex flex-col sm:flex-row gap-3">
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={placeholder}
                        className="flex-1 px-5 py-3.5 rounded-xl border text-sm focus:ring-2 focus:outline-none"
                        style={{ '--tw-ring-color': s.accent, borderColor: s.dark ? 'rgba(255,255,255,0.2)' : '#e5e7eb', backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#ffffff', color: s.text }} required />
                    <button type="submit" className="px-8 py-3.5 rounded-xl text-white text-sm font-bold whitespace-nowrap hover:opacity-90 transition-all shadow-lg hover:shadow-xl" style={{ backgroundColor: s.accent }}>
                        {submitText}
                    </button>
                </div>
            </form>
            <LeadError error={leadSubmit.error} />
        </>
    );

    // ── Minimal variant ──
    if (variant === 'minimal') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="max-w-lg mx-auto text-center">
                        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: s.text }}>{title}</h2>
                        <div className="mt-6">
                            {submitted ? <SuccessView /> : <EmailForm />}
                        </div>
                        {currentCount > 0 && !submitted && (
                            <p className="mt-4 text-sm" style={{ color: s.textSub }}>Unete a {currentCount.toLocaleString()} personas en la lista</p>
                        )}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Split variant ──
    if (variant === 'split') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        {/* Left: illustration / decorative */}
                        <div className="relative">
                            <div className="aspect-square max-w-sm mx-auto rounded-3xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${hexToRgba(s.accent, 0.15)}, ${hexToRgba(s.secondary, 0.1)})` }}>
                                <div className="text-center p-8">
                                    <div className="text-6xl mb-4">&#128640;</div>
                                    {currentCount > 0 && (
                                        <>
                                            <p className="text-4xl font-extrabold" style={{ color: s.accent }}>{currentCount.toLocaleString()}</p>
                                            <p className="text-sm mt-1" style={{ color: s.textSub }}>personas en la lista</p>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* Right: form */}
                        <div>
                            {content.tag && <span className="inline-block text-sm font-semibold tracking-wider uppercase mb-3" style={{ color: s.secondary }}>{content.tag}</span>}
                            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: s.text }}>{title}</h2>
                            {subtitle && <p className="mt-3 text-lg" style={{ color: s.textSub }}>{subtitle}</p>}
                            {description && <p className="mt-2 text-sm" style={{ color: s.textSub }}>{description}</p>}
                            <div className="mt-8">
                                {submitted ? <SuccessView /> : <EmailForm />}
                            </div>
                            <BenefitsList />
                            {currentCount > 0 && goal > 0 && !submitted && <ProgressBar />}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Centered variant (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <div className="max-w-xl mx-auto text-center">
                    {content.tag && <span className="inline-block text-sm font-semibold tracking-wider uppercase mb-3" style={{ color: s.secondary }}>{content.tag}</span>}
                    <h2 className="text-3xl md:text-5xl font-extrabold leading-tight" style={{ color: s.text }}>{title}</h2>
                    {subtitle && <p className="mt-4 text-lg" style={{ color: s.textSub }}>{subtitle}</p>}
                    {description && <p className="mt-2 text-sm" style={{ color: s.textSub }}>{description}</p>}
                    <div className="mt-8">
                        {submitted ? <SuccessView /> : <EmailForm />}
                    </div>
                    {currentCount > 0 && !submitted && (
                        <p className="mt-4 text-sm" style={{ color: s.textSub }}>Unete a <strong style={{ color: s.accent }}>{currentCount.toLocaleString()}</strong> personas en la lista</p>
                    )}
                    {currentCount > 0 && goal > 0 && !submitted && <ProgressBar />}
                    <BenefitsList />
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
